import type { ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { FormattedNumber } from '@r/components/formatted-number.tsx';
import pointsTranslation from '@r/translations/points-translation.ts';
import type {
  LogicTypeTargetStatusCompanyReadModel,
  OrderCompanyReadModel
} from '@r/api/generated/client.schemas.ts';

type TargetStatus = LogicTypeTargetStatusCompanyReadModel;
type OrdersCompany = OrderCompanyReadModel;

export const useCompanyTargetColumns = (isTargetPointLogic = false): ColumnDef<TargetStatus>[] => {
  const { t } = useTranslation();

  return [
    {
      accessorKey: 'logicTypeTarget.reference',
      header: t('admin.company.target.reference')
    },
    {
      accessorKey: 'achievedAt',
      header: t('admin.company.target.reached.at'),
      cell: ({ row }) => {
        return row.original.achievedAt
          ? new Date(row.original.achievedAt).toLocaleDateString()
          : '';
      }
    },
    {
      accessorKey: 'value',
      header: t('admin.company.target.value.current'),
      cell: ({ row }) => {
        return (
          <FormattedNumber
            value={row.original.value}
            style={isTargetPointLogic ? 'decimal' : undefined}
            suffix={
              isTargetPointLogic ? ` ${pointsTranslation(t, row.original.value, false)}` : undefined
            }
          />
        );
      }
    },
    {
      accessorKey: 'targetValue',
      header: t('admin.company.target.value'),
      cell: ({ row }) => {
        return (
          <FormattedNumber
            value={row.original.targetValue}
            style={isTargetPointLogic ? 'decimal' : undefined}
            suffix={
              isTargetPointLogic
                ? ` ${pointsTranslation(t, row.original.targetValue, false)}`
                : undefined
            }
          />
        );
      }
    }
  ];
};

export const useCompanyOrdersColumns = (): ColumnDef<OrdersCompany>[] => {
  const { t } = useTranslation();

  return [
    {
      accessorKey: 'id',
      header: t('common.id')
    },
    {
      accessorKey: 'reference',
      header: t('label.order.reference')
    },
    {
      accessorKey: 'createdAt',
      header: t('order.date.created'),
      cell: ({ row }) => {
        return row.original.createdAt ? new Date(row.original.createdAt).toLocaleDateString() : '';
      }
    },
    {
      accessorKey: 'type',
      header: t('label.order.reference'),
      cell: ({ row }) => {
        return t(`order.type.${row.original.type}`);
      }
    },
    {
      accessorKey: 'shops.0.displayName',
      header: t('label.order.shop.name')
    },
    {
      accessorKey: 'productsCount',
      header: t('order.items.count')
    },
    {
      accessorKey: 'point.amount',
      header: t('order.amount'),
      cell: ({ row }) => {
        return pointsTranslation(t, Math.abs(row.original.point?.amount ?? 0), true);
      }
    },
    {
      accessorKey: 'status',
      header: t('label.order.status'),
      cell: ({ row }) => {
        return t(row.original.status);
      }
    }
  ];
};
